import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';

const switchVariants = cva(
  [
    'peer inline-flex h-4 w-7 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        default: [
          'data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-blue-500',
          'data-[state=unchecked]:bg-grey-300 dark:data-[state=unchecked]:bg-grey-650',
          'focus-visible:ring-blue-600 dark:focus-visible:ring-blue-500',
        ],
        success: [
          'data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500',
          'data-[state=unchecked]:bg-grey-300 dark:data-[state=unchecked]:bg-grey-650',
          'focus-visible:ring-green-600 dark:focus-visible:ring-green-500',
        ],
        warning: [
          'data-[state=checked]:bg-yellow-600 dark:data-[state=checked]:bg-yellow-500',
          'data-[state=unchecked]:bg-grey-300 dark:data-[state=unchecked]:bg-grey-650',
          'focus-visible:ring-yellow-600 dark:focus-visible:ring-yellow-500',
        ],
        error: [
          'data-[state=checked]:bg-red-600 dark:data-[state=checked]:bg-red-500',
          'data-[state=unchecked]:bg-grey-300 dark:data-[state=unchecked]:bg-grey-650',
          'focus-visible:ring-red-600 dark:focus-visible:ring-red-500',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> &
  VariantProps<typeof switchVariants> & {
    className?: string;
    trackClassName?: string;
    'data-testid'?: string;
  };

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(
  (
    {
      className,
      variant,
      trackClassName,
      children,
      checked,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => (
    <SwitchPrimitives.Root
      className={cn(switchVariants({ variant }), className)}
      checked={checked}
      {...props}
      data-checked={checked}
      data-testid={dataTestId}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        asChild={!!children}
        className={cn(
          'pointer-events-none block h-3 w-3 rounded-full bg-white shadow-lg ring-0 transition-transform',
          'data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0',
          trackClassName,
        )}
      >
        {children}
      </SwitchPrimitives.Thumb>
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
